import { ChangeEvent, useCallback } from "react";
import { useRemoveClassification } from "../../SymbolEditor/hooks/useRemoveClassification";
import { clamp } from "../../../utils/math";
import { getSymbolParameterInfo } from "../getSymbolParameterInfo";
import { ClassificationParameter } from "../../../interfaces/classification";

type NumberClassifierParams = {
  onChange?: (value: number) => void;
  parameter: ClassificationParameter;
  parameterValue?: number;
};

export const useNumberClassifierUpdate = ({ onChange, parameterValue, parameter }: NumberClassifierParams) => {
  const { min, max, step, deserialize } = getSymbolParameterInfo(parameter);
  const removeClassification = useRemoveClassification(onChange, parameterValue);

  const handleChange = useCallback(
    (value: number) => {
      const stepFractionLen = getFractionLength(step);
      const result = value && truncateFraction(value, stepFractionLen);
      const rangeCheck = result !== null && !Number.isNaN(result) && result >= min && result <= max;

      if (rangeCheck && parameterValue !== value) {
        onChange(deserialize(result));
      }
    },
    [step, min, max, parameterValue, onChange, deserialize],
  );

  const handleBlur = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const value = +e.target.value.replace(/\s/g, "");

      onChange(clamp(deserialize(value), min, max));
    },
    [min, max, onChange, deserialize],
  );

  return {
    removeClassification,
    handleChange,
    handleBlur,
  } as const;
};

function getFractionLength(num: number): number {
  const [, fractionPart] = num.toString().split(".");

  if (fractionPart) {
    return fractionPart.length;
  }

  return 0;
}

function truncateFraction(num: number, digits: number): number {
  const [numberPart, fractionPart] = num.toString().split(".");

  if (fractionPart) {
    return +`${numberPart}.${fractionPart.slice(0, digits)}`;
  }

  return num;
}
